import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import ReCAPTCHA from "react-google-recaptcha";
import { countries, countryNames } from "../constants/countries";

const AirAmbulanceModal = ({ isOpen, setIsOpen }) => {
  const [formData, setFormData] = useState({
    patientName: '',
    countryCode: '+91',
    mobile: '',
    email: '',
    fromCountry: '',
    toCountry: '',
    scheduledTime: '',
    preferredLanguage: '',
    specialInstructions: '',
    documents: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [recaptchaValue, setRecaptchaValue] = useState(null);

  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LcyZ50rAAAAALJS9q7T5m_U115V1UYbM6b20PjA';

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Validate reCAPTCHA
    if (!recaptchaValue) {
      setSubmitStatus({
        type: 'error',
        message: 'Please complete the reCAPTCHA verification'
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://medical-linkers.onrender.com/api/contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.patientName,
          email: formData.email,
          mobile: formData.mobile,
          countryCode: formData.countryCode,
          preferredLanguage: formData.preferredLanguage,
          message: `Air Ambulance Request:
From: ${formData.fromCountry}
To: ${formData.toCountry}
Scheduled Time: ${formData.scheduledTime}
Special Instructions: ${formData.specialInstructions}`,
          formType: 'air-ambulance',
          country: formData.fromCountry,
          city: formData.toCountry,
          recaptchaToken: recaptchaValue
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus({ type: 'success', message: 'Thank you! Your Air Ambulance request has been submitted successfully.' });
        setFormData({
          patientName: '',
          countryCode: '+91',
          mobile: '',
          email: '',
          fromCountry: '',
          toCountry: '',
          scheduledTime: '',
          preferredLanguage: '',
          specialInstructions: '',
          documents: null
        });
        setTimeout(() => setIsOpen(false), 2000);
      } else {
        setSubmitStatus({ type: 'error', message: data.message || 'Something went wrong. Please try again.' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => setIsOpen(false)}
      >
        <Transition.Child
          as={Fragment}
          enter="transform transition ease-in-out duration-300"
          enterFrom="translate-y-full opacity-0"
          enterTo="translate-y-0 opacity-100"
          leave="transform transition ease-in-out duration-200"
          leaveFrom="translate-y-0 opacity-100"
          leaveTo="translate-y-full opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-y-full opacity-0"
              enterTo="translate-y-0 opacity-100"
              leave="transform transition ease-in-out duration-200"
              leaveFrom="translate-y-0 opacity-100"
              leaveTo="translate-y-full opacity-0"
            >
              <Dialog.Panel className="relative w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {/* Close (X) Icon */}
                <button
                  className="absolute top-4 right-4 text-gray-400 hover:text-black"
                  onClick={() => setIsOpen(false)}
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>

                <Dialog.Title
                  as="h3"
                  className="text-2xl font-bold text-[#1976f2] text-center mb-6 font-montserrat"
                >
                  Air Ambulance Request
                </Dialog.Title>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Status Message */}
                  {submitStatus && (
                    <div className={`p-3 rounded-lg font-open-sans ${
                      submitStatus.type === 'success' 
                        ? 'bg-green-100 text-green-700 border border-green-300' 
                        : 'bg-red-100 text-red-700 border border-red-300'
                    }`}>
                      {submitStatus.message}
                    </div>
                  )}

                  {/* 1st Line: Name */}
                  <input
                    type="text"
                    name="patientName"
                    placeholder="Patient's Full Name"
                    value={formData.patientName}
                    onChange={handleChange}
                    className="w-full border border-gray-300 p-3 rounded font-open-sans"
                    required
                  />

                  {/* 2nd Line: Code + Mobile */}
                  <div className="flex gap-2">
                    <select
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleChange}
                      className="w-1/3 border border-gray-300 p-3 rounded font-open-sans"
                      required
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.code + country.name} value={country.code}>
                          {country.flag} {country.name} ({country.code})
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      name="mobile"
                      placeholder="Mobile Number"
                      value={formData.mobile}
                      onChange={handleChange}
                      className="w-2/3 border border-gray-300 p-3 rounded font-open-sans"
                      required
                    />
                  </div>

                  {/* 3rd Line: Email */}
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full border border-gray-300 p-3 rounded font-open-sans"
                    required
                  />

                  {/* 4th Line: Country + Destination */}
                  <div className="flex gap-2">
                    <select
                      name="fromCountry"
                      value={formData.fromCountry}
                      onChange={handleChange}
                      className="w-1/2 border border-gray-300 p-3 rounded font-open-sans"
                      required
                    >
                      <option value="">From Country</option>
                      {countryNames.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                    <select
                      name="toCountry"
                      value={formData.toCountry}
                      onChange={handleChange}
                      className="w-1/2 border border-gray-300 p-3 rounded font-open-sans"
                      required
                    >
                      <option value="">To Country</option>
                      {countryNames.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* 5th Line: DateTime + Language */}
                  <div className="flex gap-2">
                    <input
                      type="datetime-local"
                      name="scheduledTime"
                      value={formData.scheduledTime}
                      onChange={handleChange}
                      className="w-1/2 border border-gray-300 p-3 rounded font-open-sans"
                      required
                    />
                    <input
                      type="text"
                      name="preferredLanguage"
                      placeholder="Preferred Language"
                      value={formData.preferredLanguage}
                      onChange={handleChange}
                      className="w-1/2 border border-gray-300 p-3 rounded font-open-sans"
                    />
                  </div>

                  {/* 6th Line: Instructions + Upload */}
                  <div className="flex gap-2">
                    {/* Special Instructions */}
                    <textarea
                      name="specialInstructions"
                      placeholder="Special Instructions"
                      value={formData.specialInstructions}
                      onChange={handleChange}
                      className="w-1/2 border border-gray-300 p-3 rounded h-24 font-open-sans"
                    ></textarea>

                    {/* File Upload with note */}
                    <div className="w-1/2">
                      <input
                        type="file"
                        name="documents"
                        accept="image/*,.pdf"
                        onChange={handleChange}
                        className="w-full border border-gray-300 p-3 rounded bg-white font-open-sans"
                        title="Upload travel or report documents"
                      />
                      <p className="text-xs text-gray-500 mt-1 font-open-sans">
                        Upload travel or report-related documents (PDF/Image)
                      </p>
                    </div>
                  </div>

                  {/* reCAPTCHA */}
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-center">
                      <ReCAPTCHA
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={handleRecaptchaChange}
                        onExpired={() => setRecaptchaValue(null)}
                        theme="light"
                      />
                    </div>
                    {!recaptchaValue && submitStatus?.type === 'error' && submitStatus.message.includes('reCAPTCHA') && (
                      <p className="text-red-500 text-sm text-center font-open-sans">Please complete the reCAPTCHA verification</p>
                    )}
                  </div>

                  {/* Submit Button Centered */}
                  <div className="flex justify-center">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-[#1976f2] text-white px-8 py-2 rounded-full hover:bg-blue-600 transition disabled:opacity-50 font-montserrat"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Request'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AirAmbulanceModal;
